import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../main';
import { toast } from 'react-toastify';
import { createEvento,
        createMultipleEventos,
        updateEvento,
        deleteEvento,
        deleteMultipleEventos,
        fetchEventosByDateRangeAndNivel,
        asignarClientesMasivos,
        fetchAsignacionProgreso,
        fetchEventos,
        fetchEventosByDateRangeRaw,
        cancelarClaseCompleta,
        reestablecerClaseCompleta,
        fetchClassRegister,
        markClassRegister,
        fetchMyClasses,
        fetchMyClassRegister,
        markMyClassRegister} from '../api';
import type { AttendanceStatus } from '../api/eventoService';
import { Evento, ProgressData } from '../../types/eventos';
import { useUser } from '@clerk/clerk-react';
import { useTenantCode } from './useTenantCode';



// Query hook para obtener todos los eventos
export const useEventosQuery = () => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['eventos', tenantCode],
        queryFn: fetchEventos
    });
};


// Query hook para obtener eventos por rango de fechas sin el formato del FullCalendar
export const useEventosByDateRangeQuery = (
    startDate: string,
    endDate: string,
    enabled: boolean = true
) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['eventos-date-range', startDate, endDate, tenantCode],
        queryFn: () => fetchEventosByDateRangeRaw(startDate, endDate),
        enabled: enabled && !!startDate && !!endDate
    });
};

// Mutaciones para crear eventos llamando a la función correspondiente
export const useCreateEventoMutation = (startDate: string, endDate: string) => useMutation({
    mutationFn: createEvento, 
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['eventos', startDate, endDate] });
        toast.success('Evento creado exitosamente');
    },
    // Se muestra el mensaje del servidor: en el caso de clase duplicada (409)
    // explica exactamente qué choca, y un "Error creando el Evento" genérico
    // dejaría al admin sin saber por qué no se ha creado.
    onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Error creando el Evento');
    },
});


// Mutación para crear múltiples eventos con manejo de progreso
export const useCreateMultipleEventosMutation = (startDate: string, endDate: string) => useMutation({
    mutationFn: createMultipleEventos,
    onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ['eventos', startDate, endDate] });
        toast.success(`${data.message}`);
    },
    onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Error creando eventos masivamente');
    }
});


// Mutaciones para actualizar eventos llamando a la función correspondiente
// export const useUpdateEventoMutation = (startDate: string, endDate: string) =>  useMutation({
//     mutationFn: updateEvento,
//     onSuccess: (updatedEvento) => {
//         queryClient.invalidateQueries({ queryKey: ['eventos', startDate, endDate] });
//         toast.success('Evento actualizado exitosamente');
//         // Devuelve el evento actualizado
//         return updatedEvento;
//     },
//     onError: () => {
//         toast.error('Error actualizando el Evento');
//     },
// });
export const useUpdateEventoMutation = (startDate: string, endDate: string) =>{
    const { user } = useUser();
    const userName = user?.firstName || user?.username || 'Usuario del sistema';

    return useMutation({
        mutationFn: (evento: Evento) => {
            // Añadimos el updatedBy antes de enviar
            return updateEvento({
                ...evento,
                updatedBy: userName
            });
        },
        onSuccess: (updatedEvento) => {
            // console.log('Evento actualizado:', updatedEvento);
            // P6 — sacar o meter a alguien en la clase pasa por aquí, así que la
            // lista de asistencia se queda vieja si no se invalida: seguía
            // ofreciendo marcar a quien acababas de quitar con la papelera.
            queryClient.invalidateQueries({ queryKey: ['class-register'] });
            // Si actualizamos eventos futuros, es mejor invalidar todas las consultas de eventos
            if (updatedEvento.estadisticas) {
                queryClient.invalidateQueries({ queryKey: ['eventos'] });
                // También invalidamos las consultas de plazas porque pueden haber sido afectadas
                queryClient.invalidateQueries({ queryKey: ['plazas'] });
                toast.success('Evento y todos los eventos futuros actualizados exitosamente');
                toast.info('⚠️ Si has cambiado el NIVEL de las clases, recuerda modificar la PLANTILLA DE EVENTOS.', {
                    autoClose: false,
                    closeOnClick: false,
                    draggable: true,
                    closeButton: true,
                    position: "top-center",
                    toastId: "eventos-futuros-actualizados", // ID único para evitar duplicados
                    className: "border-l-4 border-blue-500", // Opcional: estilo personalizado
                });
            } else {
                // Solo invalidamos la consulta específica si es un evento individual
                queryClient.invalidateQueries({ queryKey: ['eventos', startDate, endDate] });
                // Invalidamos plazas y clientes por si se añadió/eliminó un cliente del evento
                queryClient.invalidateQueries({ queryKey: ['plazas'] });
                queryClient.invalidateQueries({ queryKey: ['client'] });
                queryClient.invalidateQueries({ queryKey: ['clients'] });
                toast.success('Evento actualizado exitosamente');
            }
            
            // Devuelve el evento actualizado
            return updatedEvento;
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.message || 'Error actualizando el Evento';
            toast.error(errorMessage);
        },
    });
}


// Mutaciones para eliminar eventos llamando a la función correspondiente
export const useDeleteEventoMutation = (startDate: string, endDate: string) => useMutation({
    mutationFn: deleteEvento,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['eventos', startDate, endDate] });
        toast.success('Evento eliminado exitosamente');
    },
    onError: () => {
        toast.error('Error eliminando el Evento');
    },
});


// Cancelar una clase completa: compensa a cada inscrito con una recuperación
export const useCancelarClaseCompletaMutation = (startDate: string, endDate: string) => useMutation({
    mutationFn: ({ eventoId, reason, compensate, notify }: {
        eventoId: string; reason?: string; compensate?: boolean; notify?: boolean;
    }) => cancelarClaseCompleta(eventoId, reason, { compensate, notify }),
    onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ['eventos', startDate, endDate] });
        queryClient.invalidateQueries({ queryKey: ['eventos'] });
        queryClient.invalidateQueries({ queryKey: ['plazas'] });
        // Sin esto el aviso diría "0 recuperación(es) emitida(s)" cuando lo que
        // ha pasado es que no se querían emitir.
        toast.success(data.recoveriesIssued > 0
            ? `Clase cancelada. ${data.recoveriesIssued} recuperación(es) emitida(s).`
            : 'Clase cancelada. No se ha emitido ninguna recuperación.');
    },
    onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Error cancelando la clase');
    },
});

// Reestablecer una clase cancelada: revoca las recuperaciones pendientes del admin
export const useReestablecerClaseCompletaMutation = (startDate: string, endDate: string) => useMutation({
    mutationFn: (eventoId: string) => reestablecerClaseCompleta(eventoId),
    onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ['eventos', startDate, endDate] });
        queryClient.invalidateQueries({ queryKey: ['eventos'] });
        queryClient.invalidateQueries({ queryKey: ['plazas'] });
        const revoked = data.recoveriesRevoked
            ? ` ${data.recoveriesRevoked} recuperación(es) revocada(s).`
            : '';
        toast.success(`Clase reestablecida.${revoked}`);
    },
    onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Error reestableciendo la clase');
    },
});

// Mutación para eliminar múltiples eventos
export const useDeleteMultipleEventosMutation = (startDate: string, endDate: string) => useMutation({
    mutationFn: deleteMultipleEventos,
    onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ['eventos', startDate, endDate] });
        toast.success(`${data.message}`);
    },
    onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Error eliminando eventos masivamente');
    }
});



export const useEventosByDateRangeAndNivelQuery = (
    startDate: string,
    endDate: string,
    nivelId: string
) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['eventos', startDate, endDate, nivelId, tenantCode],
        queryFn: () => fetchEventosByDateRangeAndNivel(startDate, endDate, nivelId),
        enabled: !!startDate && !!endDate && !!nivelId
    });
};


// Hook para la asignacion de plazas masivas en todos los eventos
export const useAsignarClientesMasivoMutation = () => useMutation({
    mutationFn: ({ fechaInicio, fechaFin }: { fechaInicio: string; fechaFin: string }) => 
        asignarClientesMasivos(fechaInicio, fechaFin),
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['eventos'] });
        queryClient.invalidateQueries({ queryKey: ['clients'] });
        queryClient.invalidateQueries({ queryKey: ['client'] });
        toast.success('Proceso de asignación iniciado');
    },
    onError: () => {
        toast.error('Error al iniciar la asignación de clientes');
    }
});


//hook para obtener el progreso de la asignacion de plazas masivas a eventos
export const useAsignacionProgress = (sessionId?: string) => {
    return useQuery<ProgressData>({
        queryKey: ['asignacion-progress', sessionId],
        queryFn: () => fetchAsignacionProgreso(sessionId!),
        enabled: !!sessionId,
        refetchInterval: (query) => 
            query.state.data?.status === 'processing' ? 1000 : false,
        staleTime: 0,
    });
};
// ── P6 — pasar lista ─────────────────────────────────────────────────────────

// La lista de una clase. Sin caché: mientras el operador la tiene abierta otra
// persona puede añadir o sacar a alguien de la clase, y guardar sobre una lista
// vieja devuelve 409 `not_in_class`.
export const useClassRegisterQuery = (eventoId?: string, enabled = true, teaching = false) => useQuery({
    queryKey: ['class-register', eventoId, teaching],
    // Un instructor va por `/teaching`, que comprueba que la clase es suya y
    // devuelve menos datos de cada cliente. Es el MISMO panel, distinta puerta.
    queryFn: () => (teaching ? fetchMyClassRegister(eventoId!) : fetchClassRegister(eventoId!)),
    enabled: Boolean(eventoId) && enabled,
    staleTime: 0,
});

export const useMarkClassRegisterMutation = (eventoId?: string, teaching = false) => useMutation({
    mutationFn: (marks: { kind: 'client' | 'prospect'; id: string; status: AttendanceStatus }[]) =>
        (teaching ? markMyClassRegister(eventoId!, marks) : markClassRegister(eventoId!, marks)),
    onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ['class-register', eventoId] });
        // El historial del cliente gana una fila por cada marca.
        queryClient.invalidateQueries({ queryKey: ['client-activity'] });
        toast.success(data.marked === 1 ? 'Lista guardada' : `Lista guardada (${data.marked})`);
    },
    onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Error guardando la lista');
    },
});

// ── P6 fase 2 — las clases del instructor que firma ──────────────────────────

export const useMyClassesQuery = (from?: string, to?: string) => useQuery({
    queryKey: ['my-classes', from, to],
    queryFn: () => fetchMyClasses(from, to),
    staleTime: 30 * 1000,
});
